//npm packages
import React, { useEffect, useCallback, memo, useMemo, useState } from 'react';
import { connect, useSelector, shallowEqual } from 'react-redux';
import { Field, reduxForm, change } from 'redux-form';
import { Row } from 'reactstrap';

//redux actions
import { modelForm, MODELFORM } from '../../redux/actions';

//elements starts with capital letter;
import { InputField, FormBase, SelectField, Validator } from '../../components';

//helper files
import strings from '../../globalization';
import utility from '../../utility';
import consts from '../../consts';
import BasicRowColGrid from '../../utility/BasicRowColGrid';
import Swal from 'sweetalert2';

const ModelForm = memo(props => {
  const {
      dispatch,
      initialValues = {},
      comboType = {},
      formValues = {},
      err
    } = props,
    { params } = props.match;

  const [state, setState] = useState({
    isDataExportRequired: false,
    formData: {}
  });

  // define loginUserData from redux store
  const loginUserData = useSelector(state => {
    const { userDetail } = state.loginData.data || {};
    return userDetail;
  }, shallowEqual);

  const [isIncubator, setIsIncubator] = useState(false);
  const [hideField, setHideField] = useState(false);
  const [hideSave, setHideSave] = useState(false);
  const [disableReset, setDisableReset] = useState(false);

  useEffect(() => {
    if (
      Object.values(initialValues || {}).length &&
      comboType.EquipmentFamily &&
      !Object.values(formValues || {}).length
    ) {
      setHideSave(initialValues.IsComingFromSeeder);
      setDisableReset(initialValues.IsComingFromSeeder);
      onEquipmentFamilyChange({ value: initialValues.EquipmentFamilyId }, true);
      onConnectivityChanges({ value: initialValues.ConnectivityType }, true);
      onDeviceTypeChanges({ value: initialValues.DeviceType }, true);
      onDataTypeChanges({ value: initialValues.DataType }, true);
    }
    if (
      (loginUserData &&
        loginUserData.UserRole === consts.UserRoleId.SuperAdmin) ||
      loginUserData.UserRole === consts.UserRoleId.Specialist
    ) {
      setHideSave(false);
      setDisableReset(false);
    }
  }, [initialValues, formValues, comboType.EquipmentFamily, loginUserData]);

  const onEquipmentFamilyChange = ({ value }, isFirstRender) => {
    [
      consts.EquipmentFamily.NonStreamSteris,
      consts.EquipmentFamily.NonSterisHP
    ].includes(value)
      ? setHideField(true)
      : setHideField(false);
    const connectivityType = comboType.EquipmentFamily;
    const [options] =
      connectivityType.filter(option => option._id === value) || [];
    const isPlugAndPlay = consts.EquipmentType.Incubator === options?.ScopeId;
    if (!isPlugAndPlay && !isFirstRender) {
      props.change('WellCount', '');
      props.change('CycleTime', '');
    }
    setIsIncubator(isPlugAndPlay);
  };

  const onConnectivityChanges = ({ value }, isFirstRender) => {
    const connectivityType = comboType.ConnectivityType;
    const [options] = connectivityType.filter(option => option._id === value);
    const isDataExportRequired = options?.isDataExportRequired;
    if (!isDataExportRequired && !isFirstRender) {
      props.change('CATExportEquipment', '');
    }
    setState({ ...state, isDataExportRequired: isDataExportRequired });
  };
  const onDeviceTypeChanges = ({ value }, isFirstRender) => {
    const deviceType = comboType.DeviceType;
    const [options] = deviceType.filter(option => option._id === value);
    const isDataExportRequired = options?.isDataExportRequired;
    if (!isDataExportRequired && !isFirstRender) {
      props.change('CATExportEquipment', '');
    }
    setState({ ...state, isDataExportRequired: isDataExportRequired });
  };
  const onDataTypeChanges = ({ value }, isFirstRender) => {
    const dataType = comboType.DataType;
    const [options] = dataType.filter(option => option._id === value);
    const isDataExportRequired = options?.isDataExportRequired;
    if (!isDataExportRequired && !isFirstRender) {
      props.change('CATExportEquipment', '');
    }
    setState({ ...state, isDataExportRequired: isDataExportRequired });
  };

  const { isDataExportRequired } = state;

  useEffect(() => {
    dispatch(
      modelForm({
        action: 'load',
        _id: params.id,
        comboType: [
          'ConnectivityType',
          'EquipmentFamily',
          'DataExportEquipmentFamily',
          'CycleTime',
          'DeviceType',
          'DataType'
        ]
      })
    );
  }, []);

  const connectivityOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.ConnectivityType
    ) || [];

  const equipmentFamilyOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'Name' },
      comboType.EquipmentFamily
    ) || [];

  const dataExportFamilyOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.DataExportEquipmentFamily
    ) || [];

  const cycleTimeOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.CycleTime
    ) || [];

  const deviceTypeOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.DeviceType
    ) || [];

  const dataTypeOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.DataType
    ) || [];

  const onSubmit = useCallback(values => {
    const comboFields = [
      { name: 'ConnectivityType', isArray: false },
      { name: 'CATExportEquipment', isArray: false },
      { name: 'EquipmentFamilyId', isArray: false },
      { name: 'CycleTime', isArray: false },
      { name: 'DeviceType', isArray: false },
      { name: 'DataType', isArray: false }
    ];
    const data = utility.processDataBeforeSave(values, comboFields);
    Object.assign(data, { tableName: 'EquipmentModel' });
    dispatch(
      modelForm({
        action: params.id.toLowerCase() === 'new' ? 'add' : 'update',
        ...data
      })
    );
  }, []);

  return (
    <FormBase
      {...props}
      isDark={props.isDark}
      onSubmit={onSubmit}
      title={strings.EquipmentModel}
      subTitle={initialValues.ModelName}
      actionInfo={{ action: modelForm, actionName: MODELFORM }}
      hideSave={hideSave}
      disableReset={disableReset}
    >
      <Row sm="12" md="12" lg="12" style={{ width: '100%' }}>
        <BasicRowColGrid>
          {strings.EquipmentFamily}
          <Field
            name="EquipmentFamilyId"
            type="select"
            component={SelectField}
            placeholder={strings.EquipmentFamily}
            options={equipmentFamilyOptions}
            validate={Validator.required}
            onChange={value => onEquipmentFamilyChange(value)}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.ModelName}
          <Field
            name="ModelName"
            type="text"
            component={InputField}
            placeholder={strings.ModelName}
            maxLength={50}
            validate={Validator.required}
          />
        </BasicRowColGrid>
        {!hideField && (
          <BasicRowColGrid>
            {strings.ConnectivityType}
            <Field
              name="ConnectivityType"
              type="select"
              component={SelectField}
              placeholder={strings.ConnectivityType}
              options={connectivityOptions}
              validate={Validator.required}
              onChange={value => onConnectivityChanges(value)}
            />
          </BasicRowColGrid>
        )}
        {!hideField && (
          <BasicRowColGrid>
            {strings.DeviceType}
            <Field
              name="DeviceType"
              type="select"
              component={SelectField}
              placeholder={strings.DeviceType}
              options={deviceTypeOptions}
              validate={Validator.required}
              onChange={value => onDeviceTypeChanges(value)}
            />
          </BasicRowColGrid>
        )}
        {!hideField && (
          <BasicRowColGrid>
            {strings.DataType}
            <Field
              name="DataType"
              type="select"
              component={SelectField}
              placeholder={strings.DataType}
              options={dataTypeOptions}
              validate={Validator.required}
              onChange={value => onDataTypeChanges(value)}
            />
          </BasicRowColGrid>
        )}
        {!hideField && (
          <BasicRowColGrid>
            {strings.Hulu}
            <Field
              name="Hulu"
              type="checkbox"
              component={InputField}
              defaultValue={false}
            />
          </BasicRowColGrid>
        )}
        <BasicRowColGrid>
          {strings.Active}
          <Field
            name="IsActive"
            type="checkbox"
            component={InputField}
            defaultValue={true}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.UserRFID}
          <Field
            name="UserRFID"
            type="checkbox"
            component={InputField}
            defaultValue={true}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.ScopeRFID}
          <Field
            name="ScopeRFID"
            type="checkbox"
            component={InputField}
            defaultValue={true}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.SCMModel}
          <Field
            name="SCMModel"
            type="checkbox"
            component={InputField}
            defaultValue={true}
          />
        </BasicRowColGrid>
      </Row>
    </FormBase>
  );
});

const ModelReduxForm = reduxForm({
  form: 'modelForm',
  enableReinitialize: true
})(ModelForm);

const mapDispatchToProps = dispatch => ({
  change: (field, value) => dispatch(change('modelForm', field, value))
});

const mapStateToProps = state => {
  const { modelForm } = state.form;
  const { data, success, info, err } = state.modelFormData.data;
  const { isDark } = state.settingData;
  return {
    initialValues: data,
    comboType: state.comboTypes,
    isDark: isDark,
    formValues: modelForm ? modelForm.values : null,
    shouldGoBack:
      success &&
      (info === 'RecordSavedSuccessfully' ||
        info === 'RecordUpdatedSuccessfully' ||
        info === 'DeleteSuccess'),
    loginUserData: state.loginData.data,
    err
  };
};

const ModelFormModule = connect(
  mapStateToProps,
  mapDispatchToProps
)(ModelReduxForm);

export default props => {
  return <ModelFormModule {...props} />;
};
